import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Users, Store, ShoppingBag, DollarSign, Trash2, 
  Search, ChevronLeft, ChevronRight, AlertTriangle, Shield,
  Package, UserCheck, UserX, Eye, X, Loader2, BarChart2,
  TrendingUp, MousePointer, UserPlus, Rocket, Mail, Send, Crown, ExternalLink, Check, Youtube, Video, Target, Power, Globe, MessageSquare, Calendar, LayoutDashboard, Download, RefreshCw, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import YouTubeAdmin from '@/components/YouTubeAdmin';
import BroadcastAdmin from '@/components/BroadcastAdmin';
import SocialMediaAdmin from '@/components/SocialMediaAdmin';
import MarketingVideos from '@/components/MarketingVideos';
import ChatAdmin from '@/components/ChatAdmin';
import ArticleAdmin from '@/components/ArticleAdmin';
import ComplianceAdmin from '@/components/ComplianceAdmin';
import { useTwoFactorPrompt, callWith2FA } from '@/components/TwoFactorPrompt';

function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { promptFor2FA, TwoFactorPromptModal } = useTwoFactorPrompt();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // Popup-subscriber → buyer-account conversion stats. Lazily loaded
  // when the analytics tab renders so it doesn't slow the initial
  // dashboard mount.
  const [convStats, setConvStats] = useState(null);
  const [sendingFinishNudge, setSendingFinishNudge] = useState(false);
  const [campaignStats, setCampaignStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Default to 50/page so admins see "everyone" at a glance without needing
  // to paginate. Previous default (10) made it look like the platform only
  // had a handful of users on accounts with 20-100 signups.
  const [pageSize, setPageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [launchSubscribers, setLaunchSubscribers] = useState(null);
  const [sendingLaunchEmail, setSendingLaunchEmail] = useState(false);
  const [launchEmailSent, setLaunchEmailSent] = useState(false);
  const [previewingRecipients, setPreviewingRecipients] = useState(false);
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [backfillingPrices, setBackfillingPrices] = useState(false);
  const [pricingBackfillResult, setPricingBackfillResult] = useState(null);
  const [resettingOrders, setResettingOrders] = useState(false);
  const [resetOrdersResult, setResetOrdersResult] = useState(null);
  const [vipRequests, setVipRequests] = useState(null);
  const [processingVip, setProcessingVip] = useState(null);
  const [siteMode, setSiteMode] = useState('live'); // 'coming_soon' or 'live'
  const [togglingMode, setTogglingMode] = useState(false);
  const [launchDate, setLaunchDate] = useState('');
  const [savingLaunchDate, setSavingLaunchDate] = useState(false);
  const [regionStats, setRegionStats] = useState(null);
  const [creatorFeedback, setCreatorFeedback] = useState([]);
  const [creatorApplications, setCreatorApplications] = useState(null);
  const [processingApplication, setProcessingApplication] = useState(null);
  const [publicEarningsEnabled, setPublicEarningsEnabled] = useState(false);
  const [togglingEarnings, setTogglingEarnings] = useState(false);
  const [purgeStoreName, setPurgeStoreName] = useState('');
  const [purgePreview, setPurgePreview] = useState(null);
  const [purging, setPurging] = useState(false);
  const [staleCreators, setStaleCreators] = useState({ count: 0, creators: [] });
  const [loadingStale, setLoadingStale] = useState(false);
  const [staleExpanded, setStaleExpanded] = useState(false);
  const [purgingCreatorId, setPurgingCreatorId] = useState(null);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadStats();
      loadUsers();
      loadAnalytics();
      loadCampaignStats();
      loadLaunchSubscribers();
      loadVipRequests();
      loadSiteMode();
      loadRegionStats();
      loadCreatorFeedback();
      loadCreatorApplications();
      loadPublicEarningsFlag();
      loadStaleCreators();
    }
  }, [isAdmin, currentPage, roleFilter, pageSize]);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get('/admin/check');
      if (response.data.is_admin) {
        setIsAdmin(true);
      } else {
        toast.error('Admin access required');
        navigate('/');
      }
    } catch (error) {
      toast.error('Please log in to access admin panel');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadSiteMode = async () => {
    try {
      const response = await axios.get('/admin/site-mode');
      setSiteMode(response.data.mode);
      // Load locked launch date (read-only)
      const dateResponse = await axios.get('/launch-date');
      if (dateResponse.data.launch_date) {
        const date = new Date(dateResponse.data.launch_date);
        // datetime-local <input> expects "yyyy-MM-ddTHH:mm" in LOCAL time.
        // Convert the UTC instant to local clock-time so it displays
        // correctly (e.g. 19:00Z renders as 12:00 PM PDT, not 7:00 PM).
        const tzOffsetMs = date.getTimezoneOffset() * 60000;
        const local = new Date(date.getTime() - tzOffsetMs);
        setLaunchDate(local.toISOString().slice(0, 16));
      }
    } catch (error) {
      console.error('Error loading site mode:', error);
    }
  };

  const saveLaunchDate = async () => {
    if (!launchDate) {
      toast.error('Please select a launch date');
      return;
    }
    setSavingLaunchDate(true);
    try {
      await axios.post('/admin/launch-date', { launch_date: new Date(launchDate).toISOString() });
      toast.success(`Launch date updated to ${new Date(launchDate).toLocaleDateString()}!`);
    } catch (error) {
      console.error('Launch date save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save launch date');
    } finally {
      setSavingLaunchDate(false);
    }
  };

  const loadRegionStats = async () => {
    try {
      const response = await axios.get('/admin/signups-by-region');
      setRegionStats(response.data);
    } catch (error) {
      console.error('Error loading region stats:', error);
    }
  };

  const loadCreatorFeedback = async () => {
    try {
      const response = await axios.get('/admin/feedback');
      setCreatorFeedback(response.data.feedback || []);
    } catch (error) {
      console.error('Error loading feedback:', error);
    }
  };

  const markFeedbackRead = async (feedbackId) => {
    try {
      await axios.post(`/admin/feedback/${feedbackId}/mark-read`);
      loadCreatorFeedback();
    } catch (error) {
      toast.error('Failed to mark feedback as read');
    }
  };

  const loadCreatorApplications = async () => {
    try {
      const response = await axios.get('/admin/creator-applications');
      setCreatorApplications(response.data);
    } catch (error) {
      console.error('Error loading creator applications:', error);
    }
  };

  const loadPublicEarningsFlag = async () => {
    try {
      const response = await axios.get('/admin/public-earnings');
      setPublicEarningsEnabled(!!response.data?.enabled);
    } catch (error) {
      console.error('Error loading public earnings flag:', error);
    }
  };

  const togglePublicEarnings = async () => {
    const next = !publicEarningsEnabled;
    const confirmMsg = next
      ? 'Show public earnings data?\n\nThis exposes top earners, success stories, featured creators, and creator-of-the-week to every visitor. The Leaderboard page is independently gated ("Coming Soon" until manually re-enabled). Only enable AFTER real sales exist AND all seed/test creators are purged.'
      : 'Hide public earnings data?\n\nAll earnings sections will return empty on the public site.';
    if (!window.confirm(confirmMsg)) return;
    setTogglingEarnings(true);
    try {
      const response = await axios.post('/admin/public-earnings', { enabled: next });
      setPublicEarningsEnabled(!!response.data?.enabled);
      toast.success(next ? 'Public earnings ENABLED' : 'Public earnings HIDDEN');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle earnings flag');
    } finally {
      setTogglingEarnings(false);
    }
  };

  const dryRunPurgeSeed = async () => {
    const store = purgeStoreName.trim();
    if (!store) { toast.error('Enter a store name'); return; }
    setPurging(true);
    try {
      const response = await axios.post('/admin/purge-seed-creator', { store_name: store });
      setPurgePreview(response.data);
      if (!response.data?.matched_creators || response.data?.matched_creators === 0 || (Array.isArray(response.data?.matched_creators) && response.data?.matched_creators.length === 0)) {
        toast('No creator matched that store name', { icon: '⚠️' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dry run failed');
    } finally {
      setPurging(false);
    }
  };

  const confirmPurgeSeed = async () => {
    const store = purgeStoreName.trim();
    if (!store) return;
    const productCount = purgePreview?.product_count || 0;
    if (!window.confirm(`PURGE "${store}" and its ${productCount} product(s)?\n\nThis also deletes all related orders, reviews, cart items, and user activities. This cannot be undone.`)) return;
    setPurging(true);
    try {
      const response = await axios.post('/admin/purge-seed-creator', { store_name: store, confirm: true });
      toast.success(response.data?.message || 'Purge complete');
      setPurgePreview(response.data);
      setPurgeStoreName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const loadStaleCreators = async () => {
    // Fetches creators who are 21+ days past onboarding and still haven't
    // uploaded a product. This is the "day 21 alert" — after stage 3 nudge
    // (day 14) + 7 day grace period, Jake gets to decide whether to release
    // their Founding Creator spot.
    setLoadingStale(true);
    try {
      const response = await axios.get('/admin/creators/stale');
      setStaleCreators(response.data || { count: 0, creators: [] });
    } catch (error) {
      console.error('Error loading stale creators:', error);
    } finally {
      setLoadingStale(false);
    }
  };

  const purgeStaleCreator = async (creator) => {
    if (!window.confirm(`Release "${creator.store_name || creator.email}" Founding Creator spot?\n\nThis permanently deletes their creator profile so someone else can claim the spot. The user account is kept (they can re-apply). Their nudge-email history is removed.\n\nThis cannot be undone.`)) return;
    setPurgingCreatorId(creator.creator_id);
    try {
      const response = await axios.post('/admin/purge-seed-creator', { creator_id: creator.creator_id, confirm: true });
      toast.success(`Released ${creator.store_name || creator.email}'s spot`);
      // Remove from local list so the UI updates without a full reload
      setStaleCreators((prev) => ({
        count: Math.max(0, (prev.count || 1) - 1),
        creators: (prev.creators || []).filter((c) => c.creator_id !== creator.creator_id),
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purge failed');
    } finally {
      setPurgingCreatorId(null);
    }
  };

  const approveApplication = async (applicationId) => {
    setProcessingApplication(applicationId);
    try {
      await axios.post(`/admin/creator-applications/${applicationId}/approve`);
      toast.success('Creator application approved! User account created.');
      loadCreatorApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setProcessingApplication(null);
    }
  };

  const rejectApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to reject this application?')) return;
    
    setProcessingApplication(applicationId);
    try {
      await axios.post(`/admin/creator-applications/${applicationId}/reject`);
      toast.success('Application rejected');
      loadCreatorApplications();
    } catch (error) {
      toast.error('Failed to reject application');
    } finally {
      setProcessingApplication(null);
    }
  };

  const toggleSiteMode = async () => {
    const newMode = siteMode === 'coming_soon' ? 'live' : 'coming_soon';
    const confirmMsg = newMode === 'live' 
      ? '🚀 Are you sure you want to LAUNCH the site? Visitors will see the full marketplace.'
      : '⏸️ Are you sure you want to enable Coming Soon mode? Visitors will only see the countdown page.';
    
    if (!window.confirm(confirmMsg)) return;
    
    setTogglingMode(true);
    try {
      await axios.post('/admin/site-mode', { mode: newMode });
      setSiteMode(newMode);
      toast.success(newMode === 'live' 
        ? '🚀 Site is now LIVE! Marketplace is open to visitors.'
        : '⏸️ Coming Soon mode enabled. Visitors will see the countdown page.'
      );
    } catch (error) {
      toast.error('Failed to change site mode');
    } finally {
      setTogglingMode(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadAnalytics = async () => {
    // Track loading so the Refresh button can show a spinner + disabled
    // state. Previously the click made the network call silently and
    // the button felt like a no-op on mobile (where the data only
    // changes in tiny ways every few minutes).
    setAnalyticsLoading(true);
    try {
      const response = await axios.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
    // Load popup-subscriber conversion stats alongside analytics (cheap
    // call, single DB query). Failures shouldn't break the rest of the
    // dashboard — convStats just stays null and the tile won't render.
    try {
      const conv = await axios.get('/admin/popup-subscriber-conversion');
      setConvStats(conv.data);
    } catch (error) {
      console.error('Error loading subscriber conversion stats:', error);
    }
  };

  const sendFinishProfileNudge = async () => {
    if (!convStats?.unconverted_unnudged) return;
    // Show what we're about to do + dry-run preview, then require 2FA
    // (handled by callWith2FA via the backend's 401 response).
    setSendingFinishNudge(true);
    try {
      const preview = await axios.post(
        '/admin/popup-subscriber-conversion/send-finish-profile-email?dry_run=true'
      );
      const willSend = preview.data?.sent || 0;
      if (!willSend) {
        toast.info('No unconverted, unnudged popup subscribers to email.');
        return;
      }
      const ok = window.confirm(
        `This will send a "finish your profile" email to ${willSend} popup ` +
        `subscriber${willSend === 1 ? '' : 's'} who haven't created a buyer ` +
        'account yet.\n\nEach recipient is marked after send, so we never ' +
        'double-blast.\n\nProceed?'
      );
      if (!ok) return;
      const res = await callWith2FA(
        (extraHeaders = {}) => axios.post(
          '/admin/popup-subscriber-conversion/send-finish-profile-email?dry_run=false',
          {},
          { headers: { ...extraHeaders } },
        ),
        promptFor2FA,
      );
      toast.success(`Sent ${res.data?.sent || 0} finish-profile emails.`);
      // Refresh tile counts so the unnudged-count drops to 0.
      const conv = await axios.get('/admin/popup-subscriber-conversion');
      setConvStats(conv.data);
    } catch (error) {
      if (error.cancelled) return;
      console.error('Finish-profile send failed:', error);
      alert('Send failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingFinishNudge(false);
    }
  };

  const loadCampaignStats = async () => {
    try {
      const response = await axios.get('/admin/campaign-stats');
      setCampaignStats(response.data);
    } catch (error) {
      console.error('Error loading campaign stats:', error);
    }
  };

  const loadLaunchSubscribers = async () => {
    try {
      const response = await axios.get('/launch-subscribers');
      setLaunchSubscribers(response.data);
    } catch (error) {
      console.error('Error loading launch subscribers:', error);
    }
  };

  const previewLaunchRecipients = async () => {
    setPreviewingRecipients(true);
    setRecipientPreview(null);
    try {
      const response = await axios.get('/launch-subscribers/preview');
      setRecipientPreview(response.data);
    } catch (error) {
      console.error('Error previewing launch recipients:', error);
      alert('Failed to load preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPreviewingRecipients(false);
    }
  };

  const runPricingBackfill = async () => {
    if (!window.confirm('Run pricing backfill?\n\nThis sets one_time_price = monthly × 12 for every subscription product missing a valid one-time price. Idempotent — safe to run again.')) {
      return;
    }
    setBackfillingPrices(true);
    setPricingBackfillResult(null);
    try {
      const response = await axios.post('/admin/products/backfill-one-time-price');
      setPricingBackfillResult(response.data);
    } catch (error) {
      console.error('Pricing backfill failed:', error);
      alert('Backfill failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBackfillingPrices(false);
    }
  };

  const resetTestOrders = async () => {
    setResettingOrders(true);
    setResetOrdersResult(null);
    try {
      // Step 1: dry-run preview
      const preview = await axios.post('/admin/orders/reset-test-data?dry_run=true');
      const willDelete = preview.data?.would_delete || 0;
      if (willDelete === 0) {
        alert('No test orders detected. Marketplace revenue tile is already clean.');
        return;
      }
      // Step 2: confirm with sample
      const sample = (preview.data?.sample || []).slice(0, 5)
        .map(o => `  • ${o.buyer_email || '(no email)'} — $${o.total}`).join('\n');
      const ok = window.confirm(
        `Found ${willDelete} test orders (no Stripe session attached or test emails).\n\n` +
        `Sample:\n${sample}${willDelete > 5 ? `\n  ...+${willDelete - 5} more` : ''}\n\n` +
        `These will be ARCHIVED (not hard-deleted — recoverable from db.orders_archive_test) ` +
        `and removed from the Marketplace revenue tile. Proceed?`
      );
      if (!ok) return;
      // Step 3: real delete (requires fresh 2FA — will prompt automatically on first 401)
      const response = await callWith2FA(
        (extraHeaders = {}) => axios.post(
          '/admin/orders/reset-test-data?dry_run=false',
          {},
          { headers: { ...extraHeaders } }
        ),
        promptFor2FA
      );
      setResetOrdersResult(response.data);
      toast.success(`Archived ${response.data?.deleted || 0} test orders.`);
      // Refresh dashboard stats so tile updates immediately
      if (typeof loadStats === 'function') await loadStats();
    } catch (error) {
      if (error.cancelled) return; // user dismissed 2FA modal
      console.error('Reset test orders failed:', error);
      alert('Reset failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setResettingOrders(false);
    }
  };

  const sendLaunchEmail = async () => {
    if (!window.confirm('Are you sure you want to send the launch notification to ALL subscribers? This cannot be undone!')) {
      return;
    }
    
    setSendingLaunchEmail(true);
    try {
      const response = await axios.post('/launch-subscribers/send-launch-email');
      setLaunchEmailSent(true);
      toast.success(`Launch email sent to ${response.data.sent} subscribers!`);
      loadLaunchSubscribers(); // Refresh the list
    } catch (error) {
      toast.error('Failed to send launch emails');
      console.error(error);
    } finally {
      setSendingLaunchEmail(false);
    }
  };

  const loadVipRequests = async () => {
    try {
      const response = await axios.get('/vip-creator-requests');
      setVipRequests(response.data);
    } catch (error) {
      console.error('Error loading VIP requests:', error);
    }
  };

  const approveVipRequest = async (requestId) => {
    setProcessingVip(requestId);
    try {
      await axios.post(`/vip-creator-requests/${requestId}/approve`);
      toast.success('Creator approved and notified!');
      loadVipRequests();
    } catch (error) {
      toast.error('Failed to approve request');
    } finally {
      setProcessingVip(null);
    }
  };

  const rejectVipRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to reject this request?')) return;
    
    setProcessingVip(requestId);
    try {
      await axios.post(`/vip-creator-requests/${requestId}/reject`);
      toast.success('Request rejected');
      loadVipRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    } finally {
      setProcessingVip(null);
    }
  };

  const resendVipSetupLink = async (requestId) => {
    setProcessingVip(requestId);
    try {
      await axios.post(`/vip-creator-requests/${requestId}/resend-setup-link`);
      toast.success('Setup link sent! VIP will receive an email with the new link.');
      loadVipRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend setup link');
    } finally {
      setProcessingVip(null);
    }
  };

  const purgeVipCreator = async (requestId, email) => {
    if (!window.confirm(`Are you sure you want to PURGE ${email}?\n\nThis will delete:\n- VIP request\n- User account (if any)\n- Creator profile (if any)\n- All setup tokens\n\nThey will need to re-apply as a VIP.`)) {
      return;
    }
    
    setProcessingVip(requestId);
    try {
      const response = await axios.post(`/vip-creator-requests/${requestId}/purge`);
      toast.success(`Purged! ${response.data.deleted.join(', ')} deleted. ${email} can now re-apply.`);
      loadVipRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purge VIP');
    } finally {
      setProcessingVip(null);
    }
  };

  // Helper function to convert country code to flag emoji
  const getCountryFlag = (countryCode) => {
    if (!countryCode) return '🌐';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  const loadUsers = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', pageSize);
      if (roleFilter) params.append('role', roleFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`/admin/users?${params.toString()}`);
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadUsers();
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await axios.get(`/admin/users/${userId}`);
      setSelectedUser(response.data);
    } catch (error) {
      toast.error('Error loading user details');
    }
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`/admin/users/${userToDelete.user_id}`);
      toast.success(`User ${userToDelete.email} deleted successfully`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      setSelectedUser(null);
      loadUsers();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting user');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-12 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#00F0FF] hover:text-[#00C2CC] px-2 md:px-4"
            >
              <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 md:mr-2" />
              <span className="hidden md:inline">Back to Marketplace</span>
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
            <h1 className="text-base md:text-xl font-bold">Admin Panel</h1>
          </div>
          {/* Return to Admin Console - Always visible */}
          <Link
            to="/admin"
            className="flex items-center gap-1 md:gap-2 px-2 md:px-4 py-2 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/20 transition-colors text-xs md:text-sm font-medium"
          >
            <LayoutDashboard className="w-4 h-4" />
            <span className="hidden sm:inline">Return to</span> Admin Console
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-12 py-4 md:py-8">
        {/* 🚀 Site Mode Toggle */}
        <div className={`mb-8 p-6 rounded-2xl border ${
          siteMode === 'coming_soon' 
            ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30' 
            : 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30'
        }`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {siteMode === 'coming_soon' ? (
                <div className="w-14 h-14 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Rocket className="w-7 h-7 text-yellow-500" />
                </div>
              ) : (
                <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Globe className="w-7 h-7 text-green-500" />
                </div>
              )}
              <div>
                <h3 className="text-xl font-bold">
                  {siteMode === 'coming_soon' ? '⏸️ Coming Soon Mode' : '🚀 Site is LIVE'}
                </h3>
                <p className="text-sm text-neutral-400">
                  {siteMode === 'coming_soon' 
                    ? 'Visitors see the countdown page. Only you can access the full site.'
                    : 'Marketplace is open! All visitors can browse and purchase products.'
                  }
                </p>
              </div>
            </div>
            <Button
              onClick={toggleSiteMode}
              disabled={togglingMode}
              className={`min-w-[180px] font-bold ${
                siteMode === 'coming_soon'
                  ? 'bg-green-500 hover:bg-green-600 text-black'
                  : 'bg-yellow-500 hover:bg-yellow-600 text-black'
              }`}
            >
              {togglingMode ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : siteMode === 'coming_soon' ? (
                <>
                  <Power className="w-5 h-5 mr-2" />
                  Launch Site
                </>
              ) : (
                <>
                  <Power className="w-5 h-5 mr-2" />
                  Enable Coming Soon
                </>
              )}
            </Button>
          </div>
          
          {/* Launch Date Display - Locked, read-only */}
          {siteMode === 'coming_soon' && (
            <div className="mt-4 pt-4 border-t border-white/10 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-yellow-500" />
                <span className="text-sm text-neutral-300">Launch Date:</span>
              </div>
              <div className="flex items-center gap-3 bg-neutral-800/50 border border-white/10 rounded-lg px-4 py-2">
                <span className="text-white font-medium">
                  {launchDate ? new Date(launchDate).toLocaleString('en-US', { 
                    year: 'numeric', month: 'long', day: 'numeric', 
                    hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                  }) : 'Loading...'}
                </span>
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-medium">LOCKED</span>
              </div>
            </div>
          )}
        </div>

        {/* Stats Cards - Horizontal scroll on mobile */}
        {stats && (
          <PreLaunchZapPanel />
        )}
        {stats && (
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide mb-6 md:mb-8">
            <div className="flex md:grid md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4 min-w-max md:min-w-0">
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[120px] md:w-auto flex-shrink-0 md:flex-shrink">
                <Users className="w-5 h-5 md:w-6 md:h-6 text-[#00F0FF] mb-1 md:mb-2" />
                <p className="text-xl md:text-2xl font-bold">{stats.total_users}</p>
                <p className="text-xs text-neutral-400">Total Users</p>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[120px] md:w-auto flex-shrink-0 md:flex-shrink">
                <UserCheck className="w-5 h-5 md:w-6 md:h-6 text-green-500 mb-1 md:mb-2" />
                <p className="text-xl md:text-2xl font-bold">{stats.total_buyers}</p>
                <p className="text-xs text-neutral-400">Buyers</p>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[120px] md:w-auto flex-shrink-0 md:flex-shrink">
                <Store className="w-5 h-5 md:w-6 md:h-6 text-white mb-1 md:mb-2" />
                <p className="text-xl md:text-2xl font-bold">{stats.total_sellers}</p>
                <p className="text-xs text-neutral-400">Sellers</p>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[120px] md:w-auto flex-shrink-0 md:flex-shrink">
                <Package className="w-5 h-5 md:w-6 md:h-6 text-[#00F0FF] mb-1 md:mb-2" />
                <p className="text-xl md:text-2xl font-bold">{stats.total_products}</p>
                <p className="text-xs text-neutral-400">Products</p>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[120px] md:w-auto flex-shrink-0 md:flex-shrink">
                <ShoppingBag className="w-5 h-5 md:w-6 md:h-6 text-orange-500 mb-1 md:mb-2" />
                <p className="text-xl md:text-2xl font-bold">{stats.total_orders}</p>
                <p className="text-xs text-neutral-400">Orders</p>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[120px] md:w-auto flex-shrink-0 md:flex-shrink" title="Lifetime gross of completed Stripe orders (creator + house products). Affiliate partner click revenue is tracked separately — see Site Analytics → Featured Partners.">
                <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-green-400 mb-1 md:mb-2" />
                <p className="text-xl md:text-2xl font-bold">${stats.total_revenue?.toFixed(2)}</p>
                <p className="text-xs text-neutral-400">Marketplace revenue</p>
                {stats.affiliate_partner_clicks_30d > 0 && (
                  <p className="text-[10px] text-neutral-500 mt-1">+{stats.affiliate_partner_clicks_30d} partner clicks (30d)</p>
                )}
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[120px] md:w-auto flex-shrink-0 md:flex-shrink" title="All signups in the last 7 days — buyer/creator accounts + popup waitlist + launch subscribers, de-duplicated.">
                <Users className="w-5 h-5 md:w-6 md:h-6 text-blue-400 mb-1 md:mb-2" />
                <p className="text-xl md:text-2xl font-bold">{stats.recent_signups}</p>
                <p className="text-xs text-neutral-400">New signups (7d)</p>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Section */}
        {analytics && (
          <div className="mb-8" data-testid="admin-site-analytics">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <BarChart2 className="w-6 h-6 text-[#00F0FF]" />
                Site Analytics
              </h2>
              <button
                type="button"
                onClick={loadAnalytics}
                disabled={analyticsLoading}
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-200 hover:text-white border border-white/15 hover:border-white/40 disabled:opacity-60 disabled:cursor-wait rounded-full px-5 py-2.5 min-w-[120px] justify-center transition active:scale-95"
                data-testid="admin-analytics-refresh-btn"
                aria-label="Refresh site analytics"
              >
                {analyticsLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Refreshing…
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5" />
                    Refresh
                  </>
                )}
              </button>
            </div>

            {/* Three time-window columns — Today / Last 7 / Last 30 */}
            {analytics.windows && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[
                  { key: 'today', label: 'Today', data: analytics.windows.today },
                  { key: 'last_7_days', label: 'Last 7 Days', data: analytics.windows.last_7_days },
                  { key: 'last_30_days', label: 'Last 30 Days', data: analytics.windows.last_30_days },
                ].map(({ key, label, data }) => (
                  <div
                    key={key}
                    className="bg-neutral-900/60 border border-white/10 rounded-2xl p-5"
                    data-testid={`admin-analytics-window-${key}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00F0FF]">{label}</h3>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <div>
                        <dt className="text-[11px] uppercase tracking-wider text-neutral-500">Page Views</dt>
                        <dd className="text-2xl font-bold text-white tabular-nums" data-testid={`stat-${key}-page-views`}>
                          {(data?.page_views ?? 0).toLocaleString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-wider text-neutral-500">Unique Visitors</dt>
                        <dd className="text-2xl font-bold text-white tabular-nums" data-testid={`stat-${key}-unique-visitors`}>
                          {(data?.unique_visitors ?? 0).toLocaleString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-wider text-neutral-500">Signups</dt>
                        <dd className="text-2xl font-bold text-green-400 tabular-nums" data-testid={`stat-${key}-signups`}>
                          {(data?.signups ?? 0).toLocaleString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-wider text-neutral-500">Conversions</dt>
                        <dd className="text-2xl font-bold text-yellow-400 tabular-nums" data-testid={`stat-${key}-conversions`}>
                          {(data?.conversions ?? 0).toLocaleString()}
                        </dd>
                      </div>
                      <div className="col-span-2 pt-3 border-t border-white/5">
                        <dt className="text-[11px] uppercase tracking-wider text-neutral-500">Conversion Rate</dt>
                        <dd className="text-xl font-semibold text-[#00F0FF] tabular-nums" data-testid={`stat-${key}-conversion-rate`}>
                          {(data?.conversion_rate ?? 0).toFixed(2)}%
                        </dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>
            )}

            {/* Last refreshed timestamp — so you can tell when the dashboard was last pulled */}
            {analytics.generated_at && (
              <p className="text-xs text-neutral-500 mb-6" data-testid="admin-analytics-generated-at">
                Last updated: {new Date(analytics.generated_at).toLocaleString()}
              </p>
            )}

            {/* Deep-link to PostHog for full per-visitor journey + session
                recordings. Hidden when REACT_APP_POSTHOG_DASHBOARD_URL
                isn't configured so the button never appears broken. */}
            {process.env.REACT_APP_POSTHOG_DASHBOARD_URL && (
              <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-[#00F0FF]/20 bg-[#00F0FF]/[0.04] p-4">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-xs uppercase tracking-wider text-[#00F0FF] font-semibold">Deep analytics</p>
                  <p className="text-sm text-neutral-300 mt-0.5">
                    Per-visitor click stream, return-visit timeline, and session replays in PostHog.
                  </p>
                </div>
                <a
                  href={process.env.REACT_APP_POSTHOG_DASHBOARD_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="open-posthog-dashboard"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black hover:bg-[#00F0FF]/90 transition-colors"
                >
                  Open PostHog &rarr;
                </a>
              </div>
            )}

            {/* Popup-subscriber → buyer account conversion tile. Counts
                how many popup signups never created an account so you
                can see the conversion gap, and one-click sends them a
                "finish your profile" nudge email. Only renders when we
                have data and there's at least 1 popup signup. */}
            {convStats && convStats.popup_total > 0 && (
              <div className="mb-6 rounded-xl border border-yellow-400/20 bg-yellow-400/[0.04] p-4" data-testid="conv-tile">
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-xs uppercase tracking-wider text-yellow-300 font-semibold">Popup → Buyer account conversion</p>
                    <p className="text-sm text-neutral-300 mt-1">
                      <span className="text-white font-bold text-lg" data-testid="conv-unconverted">{convStats.unconverted.toLocaleString()}</span>
                      <span className="text-neutral-400"> of </span>
                      <span className="text-white font-semibold" data-testid="conv-total">{convStats.popup_total.toLocaleString()}</span>
                      <span className="text-neutral-400"> popup signups haven't created a buyer profile yet</span>
                      <span className="text-yellow-300 ml-2 font-semibold">({convStats.conversion_pct}% converted)</span>
                    </p>
                    {convStats.unconverted_unnudged > 0 && (
                      <p className="text-xs text-neutral-500 mt-1.5" data-testid="conv-unnudged">
                        {convStats.unconverted_unnudged.toLocaleString()} of those haven't been emailed the "finish your profile" nudge yet.
                      </p>
                    )}
                  </div>
                  {convStats.unconverted_unnudged > 0 && (
                    <button
                      onClick={sendFinishProfileNudge}
                      disabled={sendingFinishNudge}
                      data-testid="send-finish-profile-nudge"
                      className="inline-flex items-center gap-2 rounded-lg bg-yellow-400 hover:bg-yellow-300 px-4 py-2 text-sm font-bold text-black transition-colors disabled:opacity-50 disabled:cursor-wait"
                    >
                      {sendingFinishNudge ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4" />
                      )}
                      Send "finish profile" nudge ({convStats.unconverted_unnudged.toLocaleString()})
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Daily Chart (Simple Bar Representation) */}
            {analytics.daily_data && analytics.daily_data.length > 0 && (
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-neutral-400 mb-4">Daily Page Views (Last 7 Days)</h3>
                <div className="flex items-end gap-2 h-32">
                  {analytics.daily_data.slice(-7).map((day, idx) => {
                    const maxViews = Math.max(...analytics.daily_data.slice(-7).map(d => d.page_views || 1));
                    const height = ((day.page_views || 0) / maxViews) * 100;
                    return (
                      <div key={day.date || `day-${idx}`} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-gradient-to-t from-[#00F0FF] to-white rounded-t-sm transition-all duration-500"
                          style={{ height: `${Math.max(height, 5)}%` }}
                        />
                        <span className="text-[10px] text-neutral-500">
                          {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Traffic Sources */}
            {((analytics.traffic_sources && Object.keys(analytics.traffic_sources).length > 0) || 
              (analytics.today?.traffic_sources && Object.keys(analytics.today.traffic_sources).length > 0)) && (
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-neutral-400 mb-4">
                  Traffic Sources {Object.keys(analytics.traffic_sources || {}).length > 0 ? '(Last 30 Days)' : '(Today)'}
                </h3>
                <div className="space-y-3">
                  {Object.entries(
                    Object.keys(analytics.traffic_sources || {}).length > 0 
                      ? analytics.traffic_sources 
                      : analytics.today?.traffic_sources || {}
                  ).map(([source, count]) => {
                    const trafficData = Object.keys(analytics.traffic_sources || {}).length > 0 
                      ? analytics.traffic_sources 
                      : analytics.today?.traffic_sources || {};
                    const total = Object.values(trafficData).reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                    
                    const sourceIcons = {
                      direct: '🔗',
                      facebook: '📘',
                      instagram: '📷',
                      twitter: '🐦',
                      linkedin: '💼',
                      youtube: '📺',
                      tiktok: '🎵',
                      google: '🔍',
                      bing: '🔎',
                      other: '🌐'
                    };
                    
                    const sourceColors = {
                      direct: 'bg-gray-500',
                      facebook: 'bg-blue-600',
                      instagram: 'bg-pink-500',
                      twitter: 'bg-sky-500',
                      linkedin: 'bg-blue-700',
                      youtube: 'bg-red-600',
                      tiktok: 'bg-black',
                      google: 'bg-green-500',
                      bing: 'bg-yellow-500',
                      other: 'bg-[#00F0FF]'
                    };
                    
                    return (
                      <div key={source} className="flex items-center gap-3">
                        <span className="text-xl">{sourceIcons[source] || '🌐'}</span>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="capitalize font-medium">{source}</span>
                            <span className="text-neutral-400">{count.toLocaleString()} ({percentage}%)</span>
                          </div>
                          <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${sourceColors[source] || 'bg-[#00F0FF]'} transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Signups by Region - Right after Analytics */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#00FF94] flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Signups by Region</h2>
                <p className="text-sm text-neutral-400">Geographic distribution of your users</p>
              </div>
            </div>
            <Button 
              onClick={loadRegionStats} 
              variant="outline" 
              size="sm"
              className="border-white/20"
            >
              Refresh
            </Button>
          </div>

          {regionStats ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-neutral-800/50 rounded-xl p-4">
                  <p className="text-sm text-neutral-400 mb-1">Users with Location Data</p>
                  <p className="text-2xl font-bold text-[#00F0FF]">
                    {regionStats.total_with_location} <span className="text-sm text-neutral-400 font-normal">/ {regionStats.total_users} total</span>
                  </p>
                </div>
                <div className="bg-neutral-800/50 rounded-xl p-4">
                  <p className="text-sm text-neutral-400 mb-1">Countries Represented</p>
                  <p className="text-2xl font-bold text-[#00FF94]">{regionStats.by_country?.length || 0}</p>
                </div>
              </div>

              {/* Countries */}
              {regionStats.by_country?.length > 0 ? (
                <div>
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    🌍 Top Countries
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {regionStats.by_country.map((country, idx) => (
                      <div key={country.country_code || country.country || `country-${idx}`} className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{country.country_code ? getCountryFlag(country.country_code) : '🌐'}</span>
                          <span className="font-medium">{country.country}</span>
                        </div>
                        <span className="text-[#00F0FF] font-bold">{country.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-neutral-400">
                  <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No location data yet</p>
                  <p className="text-sm">New signups will be automatically tracked</p>
                </div>
              )}

              {/* Top Cities */}
              {regionStats.by_city?.length > 0 && (
                <div>
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    🏙️ Top Cities
                  </h3>
                  <div className="space-y-2">
                    {regionStats.by_city.slice(0, 5).map((city, idx) => (
                      <div key={`${city.city || ''}-${city.country || ''}-${idx}`} className="flex items-center justify-between p-2 bg-neutral-800/30 rounded-lg">
                        <span>{city.city}, {city.country}</span>
                        <span className="text-white font-bold">{city.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Signups with Location */}
              {regionStats.recent_signups?.length > 0 && (
                <div>
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    🆕 Recent Signups
                  </h3>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {regionStats.recent_signups.map((signup, idx) => (
                      <div key={signup.user_id || signup.email || `signup-${idx}`} className="flex items-center justify-between p-2 bg-neutral-800/30 rounded-lg text-sm">
                        <div>
                          <span className="font-medium">{signup.name || 'Anonymous'}</span>
                          <span className="text-neutral-400 ml-2">({signup.role})</span>
                        </div>
                        <div className="text-right text-neutral-400">
                          <span>{signup.location?.city}, {signup.location?.country}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
            </div>
          )}
        </div>

        {/* Launch Notification Section */}
        <div className="bg-gradient-to-br from-white/10 to-[#00F0FF]/10 border border-white/30 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Launch Notifications</h2>
                <p className="text-sm text-neutral-400">Manage pre-launch email subscribers</p>
              </div>
            </div>
            {launchSubscribers && (
              <div className="text-right">
                <p className="text-3xl font-bold text-[#00F0FF]">{launchSubscribers.count}</p>
                <p className="text-sm text-neutral-400">subscribers</p>
              </div>
            )}
          </div>
          
          {launchSubscribers?.count > 0 && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={previewLaunchRecipients}
                  disabled={previewingRecipients}
                  variant="outline"
                  className="sm:w-auto py-6 px-6 text-base font-semibold rounded-xl border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                  data-testid="preview-launch-recipients-btn"
                >
                  {previewingRecipients ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>👁  Preview recipients</>
                  )}
                </Button>
                <Button
                  onClick={sendLaunchEmail}
                  disabled={sendingLaunchEmail || launchEmailSent}
                  className={`flex-1 py-6 text-lg font-bold rounded-xl transition-all ${
                    launchEmailSent 
                      ? 'bg-green-500 hover:bg-green-500 cursor-not-allowed' 
                      : 'bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black'
                  }`}
                  data-testid="send-launch-email-btn"
                >
                  {sendingLaunchEmail ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending to {launchSubscribers.count} subscribers...
                    </>
                  ) : launchEmailSent ? (
                    <>
                      ✓ Launch Emails Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send &quot;We&apos;re Live!&quot; Email to All Subscribers
                    </>
                  )}
                </Button>
              </div>

              {recipientPreview && (
                <div className="bg-neutral-900/80 border border-[#00F0FF]/30 rounded-xl p-4 space-y-3" data-testid="recipient-preview-panel">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-xs text-neutral-400 uppercase tracking-wide">Will send to</p>
                      <p className="text-2xl font-bold text-[#00F0FF]" data-testid="preview-will-send-count">
                        {recipientPreview.will_send_to.toLocaleString()}
                      </p>
                      <p className="text-[11px] text-neutral-500 mt-1">
                        of {recipientPreview.total.toLocaleString()} total
                        {recipientPreview.already_notified > 0 && (
                          <> · skipping {recipientPreview.already_notified.toLocaleString()} already notified</>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-neutral-400 uppercase tracking-wide mb-1">By source</p>
                      <div className="flex gap-2">
                        <span className="px-2 py-1 rounded bg-[#00F0FF]/15 text-[#00F0FF] text-xs font-mono">
                          waitlist · {recipientPreview.by_source.waitlist}
                        </span>
                        <span className="px-2 py-1 rounded bg-[#00FF94]/15 text-[#00FF94] text-xs font-mono">
                          popup · {recipientPreview.by_source.popup}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-white/5">
                    <div>
                      <p className="text-[11px] text-neutral-500 mb-1.5 uppercase tracking-wide">Newest 10</p>
                      <div className="space-y-1">
                        {recipientPreview.sample_first_10?.map((r, idx) => (
                          <div key={`first-${idx}`} className="flex items-center justify-between text-xs bg-neutral-800/50 px-2 py-1.5 rounded">
                            <span className="text-neutral-300 truncate mr-2">{r.email}</span>
                            <span className={`text-[10px] uppercase ${r.source === 'popup' ? 'text-[#00FF94]' : 'text-[#00F0FF]'}`}>{r.source}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-[11px] text-neutral-500 mb-1.5 uppercase tracking-wide">Oldest 10</p>
                      <div className="space-y-1">
                        {recipientPreview.sample_last_10?.map((r, idx) => (
                          <div key={`last-${idx}`} className="flex items-center justify-between text-xs bg-neutral-800/50 px-2 py-1.5 rounded">
                            <span className="text-neutral-300 truncate mr-2">{r.email}</span>
                            <span className={`text-[10px] uppercase ${r.source === 'popup' ? 'text-[#00FF94]' : 'text-[#00F0FF]'}`}>{r.source}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-neutral-500 pt-1">
                    This is a dry-run. No emails were sent.
                  </p>
                </div>
              )}
              
              {!launchEmailSent && (
                <p className="text-xs text-neutral-500 text-center">
                  ⚠️ This will send the launch announcement email to all {launchSubscribers.count} subscribers. Only click when ready to launch!
                </p>
              )}
              
              {/* All Subscribers */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-sm text-neutral-400 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  All Subscribers ({launchSubscribers.count}):
                </p>
                <div className="max-h-60 overflow-y-auto space-y-1 pr-1">
                  {launchSubscribers.subscribers?.map((sub) => (
                    <div key={sub.subscriber_id || sub.email} className="flex items-center justify-between text-xs bg-neutral-800/60 px-3 py-2 rounded-lg" data-testid={`subscriber-row-${sub.email}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-neutral-300 truncate">{sub.email}</span>
                        {sub.source && (
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide flex-shrink-0 ${
                              sub.source === 'popup' || sub.source === 'email-capture-popup'
                                ? 'bg-[#00FF94]/15 text-[#00FF94]'
                                : 'bg-[#00F0FF]/15 text-[#00F0FF]'
                            }`}
                            title={`Captured via ${sub.source}`}
                          >
                            {sub.source === 'popup' || sub.source === 'email-capture-popup' ? 'popup' : 'waitlist'}
                          </span>
                        )}
                      </div>
                      <span className="text-neutral-500 ml-2 flex-shrink-0">{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {launchSubscribers?.count === 0 && (
            <div className="text-center py-6 text-neutral-500">
              <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No launch subscribers yet</p>
              <p className="text-sm">Share your /coming-soon page to collect emails!</p>
            </div>
          )}
        </div>

        {/* Stale Creators Alert (day-21 no-upload trigger) — only rendered
            when there's actually something to action. Each row surfaces a
            single "Release spot" button that calls the existing purge
            endpoint. Zero-noise by design: no count card, no nagging. */}
        {staleCreators.count > 0 && (
          <div className="bg-gradient-to-br from-red-500/10 to-yellow-500/5 border border-red-500/30 rounded-2xl p-6 mb-8" data-testid="stale-creators-panel">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-white">
                    {staleCreators.count} creator{staleCreators.count === 1 ? '' : 's'} still haven't listed a product
                  </h2>
                  <p className="text-sm text-neutral-400">
                    21+ days past onboarding. Nudge ladder completed. Decide whether to release their Founding spot.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setStaleExpanded((v) => !v)}
                className="border-white/15 hover:bg-white/5 flex-shrink-0"
                data-testid="stale-creators-toggle"
              >
                {staleExpanded ? 'Hide' : 'Review'}
              </Button>
            </div>

            {staleExpanded && (
              <div className="space-y-2" data-testid="stale-creators-list">
                {staleCreators.creators.map((c) => (
                  <div
                    key={c.creator_id}
                    className="bg-black/40 rounded-xl p-4 border border-white/5 flex items-start justify-between gap-4 flex-wrap md:flex-nowrap"
                    data-testid={`stale-creator-row-${c.creator_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-white truncate">{c.store_name || c.name || c.email}</span>
                        <span className="text-xs bg-red-500/15 text-red-300 px-2 py-0.5 rounded-full">
                          {c.days_since_onboard}d stale
                        </span>
                        {c.nudge_stages_sent >= 3 && (
                          <span className="text-xs bg-white/10 text-neutral-400 px-2 py-0.5 rounded-full">
                            3/3 nudges sent
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500 truncate">{c.email}</p>
                      <p className="text-xs text-neutral-600 mt-1">
                        Joined {c.created_at ? new Date(c.created_at).toLocaleDateString() : 'unknown'} · 0 products uploaded
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => purgeStaleCreator(c)}
                        disabled={purgingCreatorId === c.creator_id}
                        className="bg-red-500 hover:bg-red-600 text-white"
                        data-testid={`release-spot-btn-${c.creator_id}`}
                      >
                        {purgingCreatorId === c.creator_id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <><Trash2 className="w-4 h-4 mr-1" /> Release spot</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Public Earnings & Seed Data Control (Launch Safety) */}
        <div className="bg-black/40 border border-white/20 rounded-2xl p-6 mb-8" data-testid="public-earnings-panel">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#00FF94] flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-black" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Launch Safety — Public Earnings</h2>
              <p className="text-sm text-neutral-400">Hide or show public-facing earnings data (top earners / success stories / featured / creator-of-the-week). Leaderboard is independently gated to "Coming Soon" — see <code className="text-[#00F0FF]">pages/CreatorLeaderboard.js</code> to re-enable.</p>
            </div>
          </div>

          {/* Toggle */}
          <div className="flex items-center justify-between bg-black/30 rounded-xl p-4 border border-white/10 mb-4">
            <div className="flex-1 min-w-0 pr-4">
              <p className="font-semibold text-white mb-1">
                Public earnings are currently{' '}
                <span className={publicEarningsEnabled ? 'text-[#00FF94]' : 'text-yellow-400'}>
                  {publicEarningsEnabled ? 'VISIBLE' : 'HIDDEN'}
                </span>
              </p>
              <p className="text-xs text-neutral-400">
                {publicEarningsEnabled
                  ? 'Top Earners, Success Stories, Featured Creators, and Creator of the Week are live on the public site. (Leaderboard stays "Coming Soon" until manually re-enabled.)'
                  : 'All earnings sections return empty on the public site. Seed/test data cannot leak.'}
              </p>
            </div>
            <Button
              onClick={togglePublicEarnings}
              disabled={togglingEarnings}
              className={publicEarningsEnabled
                ? 'bg-yellow-500 hover:bg-yellow-600 text-black flex-shrink-0'
                : 'bg-[#00F0FF] hover:bg-[#00D4E0] text-black flex-shrink-0'}
              data-testid="toggle-public-earnings-btn"
            >
              {togglingEarnings ? <Loader2 className="w-4 h-4 animate-spin" /> : (publicEarningsEnabled ? 'Hide Earnings' : 'Show Earnings')}
            </Button>
          </div>

          {/* Purge seed creator */}
          <div className="bg-black/30 rounded-xl p-4 border border-red-500/20">
            <p className="font-semibold text-white mb-1 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-400" />
              Purge Seed/Test Creator
            </p>
            <p className="text-xs text-neutral-400 mb-3">
              Blast a fake creator + all their products, orders, reviews, and cart items. Dry-run first — review the match before confirming.
            </p>
            <div className="flex gap-2 mb-3 flex-wrap">
              <input
                type="text"
                value={purgeStoreName}
                onChange={(e) => { setPurgeStoreName(e.target.value); setPurgePreview(null); }}
                placeholder='e.g. "AI Tools Market"'
                className="flex-1 min-w-[180px] bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#00F0FF]/50"
                data-testid="purge-store-name-input"
              />
              <Button
                onClick={dryRunPurgeSeed}
                disabled={purging || !purgeStoreName.trim()}
                variant="outline"
                className="border-white/20 hover:bg-white/5 flex-shrink-0"
                data-testid="purge-dry-run-btn"
              >
                {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dry Run'}
              </Button>
            </div>
            {purgePreview && (
              <div className="bg-black/40 rounded-lg p-3 border border-white/10 text-xs text-neutral-300 space-y-1" data-testid="purge-preview">
                <p><strong className="text-white">Matched:</strong> {Array.isArray(purgePreview.matched_creators) ? purgePreview.matched_creators.length : purgePreview.matched_creators || 0} creator(s)</p>
                <p><strong className="text-white">Products to delete:</strong> {purgePreview.product_count || 0}</p>
                {purgePreview.sample_products?.length > 0 && (
                  <p className="text-neutral-500">Sample: {purgePreview.sample_products.slice(0, 3).map(p => p.name).filter(Boolean).join(', ')}…</p>
                )}
                {purgePreview.deleted && (
                  <p className="text-[#00FF94] mt-2"><strong>Deleted:</strong> {Object.entries(purgePreview.deleted).map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>
                )}
                {Array.isArray(purgePreview.matched_creators) && purgePreview.matched_creators.length > 0 && !purgePreview.deleted && (
                  <div className="pt-2 mt-2 border-t border-white/10">
                    <Button
                      onClick={confirmPurgeSeed}
                      disabled={purging}
                      className="bg-red-500 hover:bg-red-600 text-white w-full"
                      data-testid="purge-confirm-btn"
                    >
                      {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm Purge (${purgePreview.product_count || 0} products)`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* VIP Creator Requests */}
        <div className="bg-gradient-to-br from-[#00F0FF]/10 to-[#00FF94]/10 border border-[#00F0FF]/30 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#00FF94] flex items-center justify-center">
                <Crown className="w-6 h-6 text-black" />
              </div>
              <div>
                <h2 className="text-xl font-bold">VIP Creator Requests</h2>
                <p className="text-sm text-neutral-400">Review and approve early access applications</p>
              </div>
            </div>
            {vipRequests && (
              <div className="text-right">
                <p className="text-3xl font-bold text-[#00F0FF]">
                  {vipRequests.requests?.filter(r => r.status === 'pending').length || 0}
                </p>
                <p className="text-sm text-neutral-400">pending</p>
              </div>
            )}
          </div>
          
          {vipRequests?.requests?.filter(r => r.status === 'pending').length > 0 ? (
            <div className="space-y-3">
              {vipRequests.requests.filter(r => r.status === 'pending').map((request) => (
                <div key={request.request_id} className="bg-black/30 rounded-xl p-4 border border-white/10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-white">{request.name}</span>
                        <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">Pending</span>
                        {request.has_account && (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Account Created</span>
                        )}
                      </div>
                      <p className="text-sm text-neutral-400 mb-2">{request.email}</p>
                      {request.product_count > 0 && (
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs bg-[#00F0FF]/10 text-[#00F0FF] px-2 py-1 rounded-full flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            {request.product_count} products uploaded
                          </span>
                          {request.store_slug && (
                            <button
                              onClick={() => window.open(`/store/${request.store_slug}`, '_blank')}
                              className="text-xs text-white hover:underline flex items-center gap-1"
                            >
                              <Store className="w-3 h-3" />
                              View Store
                            </button>
                          )}
                        </div>
                      )}
                      {request.store_name && (
                        <p className="text-xs text-neutral-500 mb-1">Store: {request.store_name}</p>
                      )}
                      {request.verification_link && (
                        <a 
                          href={request.verification_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-[#00F0FF] hover:underline break-all"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {request.verification_link.length > 50 
                            ? request.verification_link.substring(0, 50) + '...' 
                            : request.verification_link}
                        </a>
                      )}
                      {request.description && (
                        <p className="text-sm text-neutral-500 mt-2 italic">&quot;{request.description}&quot;</p>
                      )}
                      <p className="text-xs text-neutral-600 mt-2">
                        Submitted: {new Date(request.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => approveVipRequest(request.request_id)}
                        disabled={processingVip === request.request_id}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        {processingVip === request.request_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rejectVipRequest(request.request_id)}
                        disabled={processingVip === request.request_id}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-neutral-500">
              <Crown className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No pending VIP requests</p>
              <p className="text-sm">Applications will appear here when creators apply</p>
            </div>
          )}
          
          {vipRequests?.requests?.filter(r => r.status === 'approved').length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-sm text-neutral-400 mb-3 flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Approved Creators: {vipRequests.requests.filter(r => r.status === 'approved').length}
              </p>
              <div className="space-y-2">
                {vipRequests.requests.filter(r => r.status === 'approved').map(request => (
                  <div key={request.request_id} className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-green-400">{request.name}</p>
                        {request.product_count > 0 && (
                          <span className="text-xs bg-[#00F0FF]/15 text-[#00F0FF] px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            {request.product_count} products
                          </span>
                        )}
                        {request.has_account && request.product_count === 0 && (
                          <span className="text-xs bg-neutral-700 text-neutral-400 px-2 py-0.5 rounded-full">No products yet</span>
                        )}
                        {!request.has_account && (
                          <span className="text-xs bg-yellow-500/15 text-yellow-400 px-2 py-0.5 rounded-full">Hasn't set up account</span>
                        )}
                      </div>
                      <p className="text-sm text-neutral-400">{request.email}</p>
                      {request.store_name && (
                        <p className="text-xs text-neutral-500 mt-0.5">Store: {request.store_name}</p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {request.store_slug && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/store/${request.store_slug}`, '_blank')}
                          className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                        >
                          <Store className="w-4 h-4 mr-1" />
                          Store
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resendVipSetupLink(request.request_id)}
                        disabled={processingVip === request.request_id}
                        className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                        data-testid={`resend-setup-link-${request.request_id}`}
                      >
                        {processingVip === request.request_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Mail className="w-4 h-4 mr-1" />
                            Resend Link
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => purgeVipCreator(request.request_id, request.email)}
                        disabled={processingVip === request.request_id}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid={`purge-vip-${request.request_id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Purge
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Creator Applications */}
        <div className="bg-gradient-to-br from-white/10 to-[#00F0FF]/10 border border-white/30 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center">
                <Store className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Creator Applications</h2>
                <p className="text-sm text-neutral-400">Review and approve new creator signups</p>
              </div>
            </div>
            {creatorApplications && (
              <div className="text-right">
                <p className="text-3xl font-bold text-white">
                  {creatorApplications.pending_count || 0}
                </p>
                <p className="text-sm text-neutral-400">pending</p>
              </div>
            )}
          </div>
          
          {creatorApplications?.applications?.filter(a => a.status === 'pending').length > 0 ? (
            <div className="space-y-3">
              {creatorApplications.applications.filter(a => a.status === 'pending').map((app) => (
                <div key={app.application_id} className="bg-black/30 rounded-xl p-4 border border-white/10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-white">{app.name}</span>
                        <span className="text-xs bg-[#00F0FF]/20 text-[#00F0FF] px-2 py-0.5 rounded-full">Pending</span>
                      </div>
                      <p className="text-sm text-neutral-400 mb-2">{app.email}</p>
                      <p className="text-sm text-[#00F0FF] mb-2">Store: {app.store_name}</p>
                      {app.portfolio_url && (
                        <a 
                          href={app.portfolio_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-[#00F0FF] hover:underline mb-2"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Portfolio
                        </a>
                      )}
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-neutral-500"><strong>Products:</strong></p>
                        <p className="text-sm text-neutral-300">{app.product_description}</p>
                        <p className="text-xs text-neutral-500 mt-2"><strong>Experience:</strong></p>
                        <p className="text-sm text-neutral-300">{app.experience}</p>
                      </div>
                      <p className="text-xs text-neutral-600 mt-2">
                        Submitted: {new Date(app.submitted_at).toLocaleDateString()} • {app.location?.city}, {app.location?.country}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => approveApplication(app.application_id)}
                        disabled={processingApplication === app.application_id}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        {processingApplication === app.application_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rejectApplication(app.application_id)}
                        disabled={processingApplication === app.application_id}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-neutral-500">
              <Store className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No pending applications</p>
              <p className="text-sm">Creator applications will appear here</p>
            </div>
          )}
          
          {creatorApplications?.applications?.filter(a => a.status === 'approved').length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-sm text-neutral-400 mb-2 flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Recently Approved: {creatorApplications.applications.filter(a => a.status === 'approved').length}
              </p>
            </div>
          )}
        </div>

        {/* Campaign Tracking - Video A/B Testing */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Campaign Tracking</h2>
                <p className="text-sm text-neutral-400">Track your video ad performance</p>
              </div>
            </div>
            <Button 
              onClick={loadCampaignStats} 
              variant="outline" 
              size="sm"
              className="border-white/20"
            >
              Refresh
            </Button>
          </div>

          {campaignStats ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-neutral-800/50 rounded-xl p-4">
                  <p className="text-sm text-neutral-400 mb-1">Total Campaign Visits</p>
                  <p className="text-2xl font-bold text-[#00F0FF]">{campaignStats.summary?.total_visits_from_campaigns || 0}</p>
                </div>
                <div className="bg-neutral-800/50 rounded-xl p-4">
                  <p className="text-sm text-neutral-400 mb-1">Unique Visitors</p>
                  <p className="text-2xl font-bold text-[#00F0FF]">{campaignStats.summary?.total_unique_visitors || 0}</p>
                </div>
                <div className="bg-neutral-800/50 rounded-xl p-4">
                  <p className="text-sm text-neutral-400 mb-1">Active Campaigns</p>
                  <p className="text-2xl font-bold text-white">{campaignStats.summary?.total_campaigns || 0}</p>
                </div>
              </div>

              {/* Video A/B Test Results */}
              {campaignStats.campaigns?.filter(c => c.utm_source === 'video_ad').length > 0 && (
                <div className="bg-neutral-800/30 rounded-xl p-4">
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Video className="w-4 h-4 text-[#00F0FF]" />
                    Video Ad Performance (A/B Test)
                  </h3>
                  <div className="space-y-3">
                    {campaignStats.campaigns
                      .filter(c => c.utm_source === 'video_ad')
                      .map((campaign, idx) => (
                        <div key={campaign.campaign_id || `${campaign.utm_campaign}-${campaign.utm_content}-${idx}`} className="flex items-center justify-between p-3 bg-neutral-900/50 rounded-lg">
                          <div>
                            <p className="font-semibold">
                              {campaign.utm_content === 'video_a_join' && '🎬 Video A - "Join Today"'}
                              {campaign.utm_content === 'video_b_start_selling' && '🎬 Video B - "Start Selling"'}
                              {campaign.utm_content === 'video_c_list_free' && '🎬 Video C - "List Free"'}
                              {!['video_a_join', 'video_b_start_selling', 'video_c_list_free'].includes(campaign.utm_content) && `🎬 ${campaign.utm_content}`}
                            </p>
                            <p className="text-xs text-neutral-400">
                              via {campaign.utm_medium} • {campaign.utm_campaign}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-[#00FF94]">{campaign.visits}</p>
                            <p className="text-xs text-neutral-400">{campaign.unique_visitors} unique</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* All Campaigns Table */}
              {campaignStats.campaigns?.length > 0 ? (
                <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
                  <table className="w-full text-xs md:text-sm min-w-[500px]">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 px-2 md:px-3 text-neutral-400 font-medium">Source</th>
                        <th className="text-left py-2 px-2 md:px-3 text-neutral-400 font-medium hidden sm:table-cell">Medium</th>
                        <th className="text-left py-2 px-2 md:px-3 text-neutral-400 font-medium">Campaign</th>
                        <th className="text-left py-2 px-2 md:px-3 text-neutral-400 font-medium hidden md:table-cell">Content</th>
                        <th className="text-right py-2 px-2 md:px-3 text-neutral-400 font-medium">Visits</th>
                        <th className="text-right py-2 px-2 md:px-3 text-neutral-400 font-medium hidden sm:table-cell">Unique</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaignStats.campaigns.map((campaign, idx) => (
                        <tr key={campaign.campaign_id || `${campaign.utm_source}-${campaign.utm_campaign}-${campaign.utm_content}-${idx}`} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-2 px-2 md:px-3 truncate max-w-[80px] md:max-w-none">{campaign.utm_source}</td>
                          <td className="py-2 px-2 md:px-3 hidden sm:table-cell">{campaign.utm_medium}</td>
                          <td className="py-2 px-2 md:px-3 truncate max-w-[80px] md:max-w-none">{campaign.utm_campaign}</td>
                          <td className="py-2 px-2 md:px-3 text-[#00F0FF] hidden md:table-cell">{campaign.utm_content}</td>
                          <td className="py-2 px-2 md:px-3 text-right font-bold">{campaign.visits}</td>
                          <td className="py-2 px-2 md:px-3 text-right text-neutral-400 hidden sm:table-cell">{campaign.unique_visitors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-neutral-400">
                  <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No campaign data yet</p>
                  <p className="text-sm mt-1">Share your video links with UTM parameters to start tracking!</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-400">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              <p>Loading campaign data...</p>
            </div>
          )}
        </div>

        {/* Marketing Videos */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-3 md:p-6 mb-6 md:mb-8 overflow-hidden">
          <MarketingVideos />
        </div>

        {/* Print Marketing — SMB Flyers */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-6 md:mb-8" data-testid="smb-flyer-card">
          <div className="mb-4">
            <h2 className="text-xl font-bold mb-1">Print Marketing — 4×6 Flyers</h2>
            <p className="text-sm text-neutral-400 max-w-xl">
              Brand-compliant promotional flyers for handing out at conferences, coworking
              spaces, and SMB events. Each has a tracked QR code so leads attribute back
              to the print campaign.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Seller flyer */}
            <div className="border border-white/10 rounded-xl p-4 flex flex-col gap-3" data-testid="seller-flyer-tile">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/15 border border-[#00F0FF]/40 flex items-center justify-center shrink-0">
                  <Send className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-sm">For Creators / Sellers</p>
                  <p className="text-xs text-neutral-500">"Sell your AI tool. Get paid this week."</p>
                </div>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/marketing-assets/smb-flyer.pdf`}
                download="theailedger-smb-flyer-4x6.pdf"
                className="inline-flex items-center justify-center gap-2 bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold rounded-lg px-4 py-2.5 transition-colors text-sm"
                data-testid="download-smb-flyer-btn"
              >
                <Download className="w-4 h-4" />
                Download Seller PDF
              </a>
            </div>

            {/* Buyer flyer */}
            <div className="border border-white/10 rounded-xl p-4 flex flex-col gap-3" data-testid="buyer-flyer-tile">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00FF94]/15 border border-[#00FF94]/40 flex items-center justify-center shrink-0">
                  <Target className="w-5 h-5 text-[#00FF94]" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-sm">For SMB Buyers</p>
                  <p className="text-xs text-neutral-500">"Skip the AI hype. Find tools that work."</p>
                </div>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/marketing-assets/buyer-flyer.pdf`}
                download="theailedger-smb-buyer-flyer-4x6.pdf"
                className="inline-flex items-center justify-center gap-2 bg-[#00FF94] hover:bg-[#00CC75] text-black font-bold rounded-lg px-4 py-2.5 transition-colors text-sm"
                data-testid="download-buyer-flyer-btn"
              >
                <Download className="w-4 h-4" />
                Download Buyer PDF
              </a>
            </div>
          </div>
        </div>

        {/* Social Media Content */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-3 md:p-6 mb-6 md:mb-8 overflow-hidden">
          <SocialMediaAdmin />
        </div>

        {/* YouTube Content Automation */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-3 md:p-6 mb-6 md:mb-8 overflow-hidden">
          <YouTubeAdmin />
        </div>

        {/* Broadcast Center */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-8">
          <BroadcastAdmin />
        </div>

        {/* Chat Management */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-8">
          <ChatAdmin />
        </div>

        {/* Article Management */}
        <div className="mb-8">
          <ArticleAdmin />
        </div>

        {/* Compliance & Security */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-8">
          <ComplianceAdmin />
        </div>

        {/* Quick Admin Tools — direct links to single-purpose admin utilities
            that don't deserve a full section on this overview. Add new ones
            here as the toolkit grows. */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-8" data-testid="admin-quick-tools">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#0080A0] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-black" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Quick Admin Tools</h2>
              <p className="text-sm text-neutral-400">One-off utilities — no terminal required</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <button
              onClick={() => navigate('/admin/avatar-tool')}
              data-testid="quick-tool-avatar"
              className="text-left p-4 rounded-xl border border-white/10 bg-black/40 hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/[0.03] transition-colors group"
            >
              <div className="text-sm font-bold text-white mb-1 group-hover:text-[#00F0FF]">
                Avatar Tool
              </div>
              <div className="text-xs text-neutral-400 leading-snug">
                Force-set any creator's storefront avatar by email / slug / id. Upload or paste a URL.
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/product-image-tool')}
              data-testid="quick-tool-product-image"
              className="text-left p-4 rounded-xl border border-white/10 bg-black/40 hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/[0.03] transition-colors group"
            >
              <div className="text-sm font-bold text-white mb-1 group-hover:text-[#00F0FF]">
                Product Image Tool
              </div>
              <div className="text-xs text-neutral-400 leading-snug">
                Override a product's thumbnail by product_id or slug. Upload, paste, replace or prepend.
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/user-merge-tool')}
              data-testid="quick-tool-user-merge"
              className="text-left p-4 rounded-xl border border-amber-400/20 bg-amber-400/[0.03] hover:border-amber-400/40 hover:bg-amber-400/[0.06] transition-colors group"
            >
              <div className="text-sm font-bold text-amber-200 mb-1 group-hover:text-amber-100">
                User Merge Tool
              </div>
              <div className="text-xs text-neutral-400 leading-snug">
                Heal duplicate creator accounts (case-sensitive email split). Find by email → merge orphaned sessions/orders/sub-affiliates into the canonical user.
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/data-sync-tool')}
              data-testid="quick-tool-data-sync"
              className="text-left p-4 rounded-xl border border-red-400/20 bg-red-400/[0.03] hover:border-red-400/40 hover:bg-red-400/[0.06] transition-colors group"
            >
              <div className="text-sm font-bold text-red-200 mb-1 group-hover:text-red-100">
                Data Sync &amp; Creator Repair
              </div>
              <div className="text-xs text-neutral-400 leading-snug">
                Fix "all my setups missing" reports. Detect creators without a profile row, orphan creators, approved-but-not-signed-up VIPs. One-click heal.
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/influencers')}
              data-testid="quick-tool-influencers"
              className="text-left p-4 rounded-xl border border-white/10 bg-black/40 hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/[0.03] transition-colors group"
            >
              <div className="text-sm font-bold text-white mb-1 group-hover:text-[#00F0FF]">
                Influencer Program
              </div>
              <div className="text-xs text-neutral-400 leading-snug">
                Track + manage influencer affiliate codes and outreach.
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/affiliate-partners')}
              data-testid="quick-tool-partners"
              className="text-left p-4 rounded-xl border border-white/10 bg-black/40 hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/[0.03] transition-colors group"
            >
              <div className="text-sm font-bold text-white mb-1 group-hover:text-[#00F0FF]">
                Affiliate Partners
              </div>
              <div className="text-xs text-neutral-400 leading-snug">
                Manage the partner directory shown on /partners.
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/qr-codes')}
              data-testid="quick-tool-qr-codes"
              className="text-left p-4 rounded-xl border border-white/10 bg-black/40 hover:border-[#FF8C00]/40 hover:bg-[#FF8C00]/[0.04] transition-colors group"
            >
              <div className="text-sm font-bold text-white mb-1 group-hover:text-[#FF8C00]">
                Postcard QR Codes
              </div>
              <div className="text-xs text-neutral-400 leading-snug">
                Mint trackable QR codes for postcard mailings. Single or bulk (per-zip). Stats: scans, signups, orders.
              </div>
            </button>
            <button
              onClick={resetTestOrders}
              disabled={resettingOrders}
              data-testid="quick-tool-reset-test-orders"
              className="text-left p-4 rounded-xl border border-red-400/20 bg-red-400/[0.03] hover:border-red-400/40 hover:bg-red-400/[0.06] transition-colors group disabled:opacity-50 disabled:cursor-wait"
            >
              <div className="text-sm font-bold text-red-200 mb-1 group-hover:text-red-100 flex items-center gap-2">
                {resettingOrders && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Reset Marketplace Revenue Tile
              </div>
              <div className="text-xs text-neutral-400 leading-snug">
                Archive test/seed orders (no Stripe session or test emails) so the Marketplace revenue tile counts only real sales going forward. Shows preview first, fully reversible.
              </div>
              {resetOrdersResult && (
                <div className="mt-2 text-[11px] text-red-300 font-mono" data-testid="reset-orders-result">
                  ✓ Archived {resetOrdersResult.deleted} test orders
                </div>
              )}
            </button>
            <button
              onClick={runPricingBackfill}
              disabled={backfillingPrices}
              data-testid="quick-tool-price-backfill"
              className="text-left p-4 rounded-xl border border-emerald-400/20 bg-emerald-400/[0.03] hover:border-emerald-400/40 hover:bg-emerald-400/[0.06] transition-colors group disabled:opacity-50 disabled:cursor-wait"
            >
              <div className="text-sm font-bold text-emerald-200 mb-1 group-hover:text-emerald-100 flex items-center gap-2">
                {backfillingPrices && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Fix Subscription One-Time Prices
              </div>
              <div className="text-xs text-neutral-400 leading-snug">
                For every subscription product, sets the one-time/lifetime price to 12× monthly so it never equals the /mo rate. Idempotent.
              </div>
              {pricingBackfillResult && (
                <div className="mt-2 text-[11px] text-emerald-300 font-mono" data-testid="price-backfill-result">
                  ✓ Fixed {pricingBackfillResult.fixed_count} · Skipped {pricingBackfillResult.skipped_count}
                  {pricingBackfillResult.fixed_count > 0 && (
                    <div className="mt-1 text-neutral-400 normal-case">
                      {pricingBackfillResult.fixed.slice(0, 4).map(p => p.name).join(', ')}
                      {pricingBackfillResult.fixed.length > 4 && ` +${pricingBackfillResult.fixed.length - 4} more`}
                    </div>
                  )}
                </div>
              )}
            </button>
          </div>
        </div>


        {/* Creator Feedback */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00FF94] to-[#00F0FF] flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Creator Feedback</h2>
                <p className="text-sm text-neutral-400">Feedback from your creators</p>
              </div>
            </div>
            {creatorFeedback.filter(f => f.status === 'new').length > 0 && (
              <span className="px-3 py-1 rounded-full bg-[#00FF94]/20 text-[#00FF94] text-sm">
                {creatorFeedback.filter(f => f.status === 'new').length} new
              </span>
            )}
          </div>

          {creatorFeedback.length === 0 ? (
            <div className="text-center py-8 text-neutral-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No feedback received yet</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {creatorFeedback.map((feedback) => (
                <div 
                  key={feedback.feedback_id}
                  className={`p-4 rounded-xl border ${
                    feedback.status === 'new' 
                      ? 'bg-[#00FF94]/5 border-[#00FF94]/30' 
                      : 'bg-neutral-800/30 border-white/5'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {feedback.status === 'new' && (
                          <span className="w-2 h-2 rounded-full bg-[#00FF94]"></span>
                        )}
                        <h4 className="font-semibold">{feedback.subject}</h4>
                      </div>
                      <p className="text-sm text-neutral-300 mb-2">{feedback.message}</p>
                      <div className="flex items-center gap-3 text-xs text-neutral-500">
                        <span>From: {feedback.user_name} ({feedback.user_email})</span>
                        <span>•</span>
                        <span>{new Date(feedback.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {feedback.status === 'new' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markFeedbackRead(feedback.feedback_id)}
                        className="border-[#00FF94]/30 text-[#00FF94] hover:bg-[#00FF94]/10"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User Management */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h2 className="text-xl font-bold mb-4">User Management</h2>
            
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by email or name..."
                    className="pl-10 bg-neutral-800 border-white/10"
                  />
                </div>
                <Button type="submit" className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black">
                  Search
                </Button>
              </form>
              
              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setCurrentPage(1);
                }}
                data-testid="admin-users-role-filter"
                className="bg-neutral-800 border border-white/10 rounded-lg px-4 py-2 text-sm"
              >
                <option value="">All Roles</option>
                <option value="buyer">Buyers</option>
                <option value="creator">Sellers</option>
                <option value="admin">Admins</option>
              </select>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                data-testid="admin-users-page-size"
                title="Users per page"
                className="bg-neutral-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
                <option value={500}>500 / page</option>
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-neutral-800/50">
                <tr>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-neutral-400 uppercase">User</th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-neutral-400 uppercase">Role</th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-neutral-400 uppercase hidden sm:table-cell">Store/Orders</th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-neutral-400 uppercase">Status</th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-neutral-400 uppercase hidden md:table-cell">Joined</th>
                  <th className="px-4 md:px-6 py-3 text-right text-xs font-semibold text-neutral-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-white/5">
                    <td className="px-4 md:px-6 py-4">
                      <div>
                        <p className="font-medium text-sm md:text-base truncate max-w-[150px] md:max-w-none">{user.name}</p>
                        <p className="text-xs md:text-sm text-neutral-400 truncate max-w-[150px] md:max-w-none">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.role === 'creator' 
                          ? 'bg-[#00FF94]/20 text-[#00FF94]' 
                          : 'bg-[#00F0FF]/20 text-[#00F0FF]'
                      }`}>
                        {user.role === 'creator' ? 'Seller' : 'Buyer'}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4 text-sm text-neutral-400 hidden sm:table-cell">
                      {user.role === 'creator' ? (
                        <span>{user.store_name || 'No store'} ({user.product_count || 0} products)</span>
                      ) : (
                        <span>{user.order_count || 0} orders</span>
                      )}
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      {user.email_verified ? (
                        <span className="flex items-center gap-1 text-green-400 text-xs md:text-sm">
                          <UserCheck className="w-3 h-3 md:w-4 md:h-4" /> <span className="hidden md:inline">Verified</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-yellow-400 text-xs md:text-sm">
                          <UserX className="w-3 h-3 md:w-4 md:h-4" /> <span className="hidden md:inline">Unverified</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 md:px-6 py-4 text-sm text-neutral-400 hidden md:table-cell">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 md:px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1 md:gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewUserDetails(user.user_id)}
                          className="text-[#00F0FF] hover:text-[#00C2CC] p-1 md:p-2"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDelete(user)}
                          className="text-red-400 hover:text-red-300 p-1 md:p-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-white/10 flex items-center justify-between">
            <p className="text-sm text-neutral-400">
              Showing {users.length} of {totalUsers} users
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="border-white/10"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="border-white/10"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-xl font-bold">User Details</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="text-sm font-semibold text-neutral-400 mb-2">Basic Information</h4>
                <div className="bg-neutral-800/50 rounded-xl p-4 space-y-2">
                  <p><span className="text-neutral-400">Name:</span> {selectedUser.name}</p>
                  <p><span className="text-neutral-400">Email:</span> {selectedUser.email}</p>
                  <p><span className="text-neutral-400">Role:</span> {selectedUser.role === 'creator' ? 'Seller' : 'Buyer'}</p>
                  <p><span className="text-neutral-400">Joined:</span> {new Date(selectedUser.created_at).toLocaleString()}</p>
                  <p><span className="text-neutral-400">Email Verified:</span> {selectedUser.email_verified ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {/* Seller Info */}
              {selectedUser.creator_info && (
                <div>
                  <h4 className="text-sm font-semibold text-neutral-400 mb-2">Store Information</h4>
                  <div className="bg-neutral-800/50 rounded-xl p-4 space-y-2">
                    <p><span className="text-neutral-400">Store Name:</span> {selectedUser.creator_info.store_name}</p>
                    <p><span className="text-neutral-400">Store Slug:</span> {selectedUser.creator_info.store_slug}</p>
                    <p><span className="text-neutral-400">Stripe Connected:</span> {selectedUser.creator_info.stripe_account_id ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              )}

              {/* Products */}
              {selectedUser.products && selectedUser.products.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-neutral-400 mb-2">Products ({selectedUser.products.length})</h4>
                  <div className="bg-neutral-800/50 rounded-xl p-4 space-y-2 max-h-40 overflow-y-auto">
                    {selectedUser.products.map(product => (
                      <div key={product.product_id} className="flex justify-between items-center">
                        <span className="text-sm">{product.name}</span>
                        <span className="text-sm text-[#00F0FF]">${product.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Orders */}
              {selectedUser.recent_orders && selectedUser.recent_orders.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-neutral-400 mb-2">Recent Orders</h4>
                  <div className="bg-neutral-800/50 rounded-xl p-4 space-y-2 max-h-40 overflow-y-auto">
                    {selectedUser.recent_orders.map(order => (
                      <div key={order.order_id} className="flex justify-between items-center text-sm">
                        <span className="text-neutral-400">{new Date(order.created_at).toLocaleDateString()}</span>
                        <span className="text-[#00F0FF]">${order.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delete Button */}
              <div className="pt-4 border-t border-white/10">
                <Button
                  onClick={() => confirmDelete(selectedUser)}
                  className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete This User
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 border border-red-500/30 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Delete User?</h3>
                <p className="text-sm text-neutral-400">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="bg-neutral-800/50 rounded-xl p-4 mb-6">
              <p className="text-sm mb-2">
                <span className="text-neutral-400">Email:</span> {userToDelete.email}
              </p>
              <p className="text-sm mb-2">
                <span className="text-neutral-400">Name:</span> {userToDelete.name}
              </p>
              <p className="text-sm">
                <span className="text-neutral-400">Role:</span> {userToDelete.role === 'creator' ? 'Seller' : 'Buyer'}
              </p>
            </div>

            {userToDelete.role === 'creator' && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-yellow-300">
                  <strong>Warning:</strong> This will also delete all products, affiliate programs, and store data associated with this seller.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                className="flex-1 border-white/10"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={deleteUser}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete User
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      <TwoFactorPromptModal />
    </div>
  );
}

export default AdminDashboard;

/**
 * Pre-launch test-data cleanup panel.
 *
 * Surfaces two buttons:
 *   1. Preview — calls /api/admin/pre-launch-zap/preview, shows counts.
 *   2. Zap — requires typing "DELETE" then POSTs the execute endpoint.
 *
 * Hidden once the site is live (siteMode !== 'coming_soon' would mean
 * orders are real and shouldn't be bulk-deleted by this tool).
 * Intentionally minimal — this is a one-shot tool, not a permanent
 * surface in the dashboard.
 */
function PreLaunchZapPanel() {
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState('');

  const headers = () => {
    const tok = localStorage.getItem('session_token') || localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  const loadPreview = async () => {
    setBusy(true);
    try {
      const res = await axios.get('/admin/pre-launch-zap/preview', { headers: headers() });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setBusy(false);
    }
  };

  const zap = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Type DELETE in the box to confirm.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        '/admin/pre-launch-zap/execute?confirm=YES_ZAP_PRE_LAUNCH_DATA',
        {}, { headers: headers() }
      );
      toast.success(res.data.message, { duration: 8000 });
      setPreview(null);
      setConfirmText('');
      // Refresh the stats card so the user sees "0 Orders" immediately
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zap failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 bg-amber-500/[0.06] border border-amber-500/30 rounded-2xl p-5"
         data-testid="pre-launch-zap-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-amber-300 flex items-center gap-2">
            🧹 Pre-launch cleanup — wipe test orders + chat history
          </h3>
          <p className="text-xs text-neutral-400 mt-1 leading-relaxed max-w-2xl">
            Use this <b>once</b> right before public launch to zero out the stats
            (pending/abandoned/failed orders, all chat messages and threads).
            <b className="text-amber-300"> Completed/paid orders are NEVER deleted.</b>
            {' '}Chat room definitions stay intact — only the messages clear.
          </p>
        </div>
        <button
          onClick={loadPreview}
          disabled={busy}
          data-testid="zap-preview-btn"
          className="px-4 py-2 bg-amber-500/15 border border-amber-500/40 rounded-lg text-amber-300 text-sm font-semibold hover:bg-amber-500/25 disabled:opacity-50"
        >
          {preview ? 'Refresh preview' : 'Preview what will delete'}
        </button>
      </div>

      {preview && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-red-500/[0.06] border border-red-500/30 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wide text-red-300 font-bold mb-2">
              Will delete
            </div>
            {Object.entries(preview.will_delete).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm py-0.5">
                <span className="text-neutral-300">{k.replace(/_/g, ' ')}</span>
                <span className="text-red-400 font-bold">{v}</span>
              </div>
            ))}
          </div>
          <div className="bg-emerald-500/[0.06] border border-emerald-500/30 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wide text-emerald-300 font-bold mb-2">
              Will keep (real)
            </div>
            {Object.entries(preview.will_keep).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm py-0.5">
                <span className="text-neutral-300">{k.replace(/_/g, ' ')}</span>
                <span className="text-emerald-400 font-bold">{v}</span>
              </div>
            ))}
          </div>

          <div className="md:col-span-2 flex items-center gap-2 mt-2">
            <input
              type="text"
              placeholder='Type "DELETE" to confirm'
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="zap-confirm-input"
              className="flex-1 bg-black/40 border border-red-500/40 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-500"
            />
            <button
              onClick={zap}
              disabled={busy || confirmText !== 'DELETE'}
              data-testid="zap-execute-btn"
              className="px-5 py-2 bg-red-500 text-white text-sm font-bold rounded-lg hover:bg-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Zap now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
